import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  Tab,
  Tabs,
} from '@mui/material';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import ReactionIcon from 'components/common/ReactionIcon';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-redux-i18n';
import Message from 'services/api/Message';
import messagesActions from 'store/app/entities/messages/action';
import authUserId from 'store/selectors/authUserId';
import { I18n } from 'utils/i18n';

const ReactionDetails = ({ messageId, anchorRef, isOpen, close }) => {
  const dispatch = useDispatch();
  const [reactions, setReactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');
  const [usersMap, setUsersMap] = useState({});
  const currentUserId = useSelector(authUserId);

  const handleRemoveReaction = useCallback(
    async reaction => {
      try {
        await dispatch(messagesActions.removeReaction(messageId, reaction));

        const { reactions: messageReactions, users } = await Message.getReactions(messageId);
        setReactions(messageReactions);
        setUsersMap(users);
      } catch (error) {
        console.error('Failed to remove reaction:', error);
      }
    },
    [messageId, dispatch],
  );

  useEffect(() => {
    const loadReactions = async () => {
      if (!isOpen) return;
      try {
        const { reactions: messageReactions, users } = await Message.getReactions(messageId);
        setReactions(messageReactions);
        setUsersMap(users);
      } finally {
        setIsLoading(false);
      }
    };

    loadReactions();
  }, [messageId, isOpen]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const totalReactions = useMemo(
    () => reactions.reduce((sum, r) => sum + r.users.length, 0),
    [reactions],
  );

  const filteredUsers = useMemo(
    () =>
      selectedTab === 'all'
        ? reactions.flatMap(r =>
            r.users.map(userId => ({
              id: userId,
              reaction: r.name,
              ...usersMap[userId],
            })),
          )
        : reactions
            .find(r => r.name === selectedTab)
            ?.users.map(userId => ({
              id: userId,
              ...usersMap[userId],
            })) || [],
    [selectedTab, reactions, usersMap],
  );

  return (
    <Popover
      open={isOpen && !isLoading}
      anchorEl={anchorRef.current}
      onClose={close}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: theme => ({
            maxWidth: 300,
            minWidth: 250,
            position: 'relative',
            overflow: 'visible',
            backgroundColor: 'transparent',
            transform: 'translateY(-4px)',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-4px',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 8,
              height: 8,
              border: `1px solid ${theme.palette.divider}`,
              borderTop: 'none',
              borderLeft: 'none',
              backgroundColor: 'transparent',
            },
          }),
        },
      }}
      style={{ top: '-4px' }}
    >
      <Box
        sx={theme => ({
          backdropFilter: 'blur(16px)',
          borderColor: theme.palette.divider,
          borderWidth: 1,
          borderStyle: 'solid',
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(26, 28, 31, 0.7)' : 'rgba(248, 249, 253, 0.4)',
          borderRadius: '8px',
          clipPath:
            'polygon(0 0, 100% 0, 100% 100%, calc(50% + 5px) 100%, calc(50% + 5px) calc(100% - 1px), calc(50% - 5px) calc(100% - 1px), calc(50% - 5px) 100%, 0 100%)',
        })}
      >
        <Box>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={theme => ({
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            <Tab
              value="all"
              label={
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Translate value="ReactionDetails.All" />
                  <span>{totalReactions}</span>
                </Box>
              }
            />
            {reactions.map(r => (
              <Tab
                key={r.name}
                value={r.name}
                label={
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <ReactionIcon name={r.name} size={16} />
                    <span>{r.users.length}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
          <List
            sx={{
              p: 0.5,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 100,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              filteredUsers.map(user => (
                <ListItem key={`${user.id}-${user.reaction || ''}`} disablePadding sx={{ p: 0 }}>
                  <ListItemButton
                    sx={{
                      py: 0.25,
                      px: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                    onClick={() =>
                      user.id === currentUserId ? handleRemoveReaction(user.reaction) : null
                    }
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <EmployeeAvatar employee={user} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.name}
                      secondary={
                        user.id === currentUserId ? I18n.t('ReactionDetails.ClickToRemove') : null
                      }
                      sx={{
                        m: 0,
                        py: 0.25,
                        minHeight: 0,
                        '& .MuiTypography-primary': {
                          fontSize: '0.95rem',
                        },
                        '& .MuiTypography-secondary': {
                          fontSize: '0.85rem',
                          color: 'text.secondary',
                        },
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ width: 20, height: 20 }}>
                        <ReactionIcon name={selectedTab === 'all' ? user.reaction : selectedTab} />
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Box>
    </Popover>
  );
};

ReactionDetails.propTypes = {
  messageId: PropTypes.string.isRequired,
  anchorRef: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

export default ReactionDetails;
